import type { DayOfWeek } from "@repo/api";
import type { AvailabilityBlockDto, ScheduleDto } from "@repo/data-store";
import { DAY_INDEX_TO_CODE } from "./constants";

/** Parsed availability from SchedulingParameters extension */
export interface ParsedAvailability {
  dayOfWeek: string[]; // ["mon", "tue", ...]
  startTime: string; // "09:00"
  endTime: string; // "17:00"
}

/** Expanded blocked time range */
export interface ExpandedBlock {
  start: Date;
  end: Date;
}

/** Parse SchedulingParameters extension from Schedule resource */
export function parseSchedulingParams(schedule: ScheduleDto | null | undefined): ParsedAvailability | null {
  const avail = schedule?.schedulingParameters?.[0]?.availability;
  if (!avail) return null;
  return { dayOfWeek: avail.dayOfWeek, startTime: avail.startTime, endTime: avail.endTime };
}

/** Check if schedule is inactive */
export function isDateBlocked(_date: Date, schedule: { active?: boolean } | null | undefined): boolean {
  if (!schedule || schedule.active === false) return true;
  return false;
}

/** Check if day of week is blocked based on availability */
export function isDayOfWeekBlocked(date: Date, availability: ParsedAvailability | null): boolean {
  if (!availability) return false;
  const dayCode = DAY_INDEX_TO_CODE[date.getDay()];
  return !availability.dayOfWeek.includes(dayCode);
}

/** Expand a recurring block into actual date ranges for the view range */
export function expandRecurringBlock(slot: AvailabilityBlockDto, viewStart: Date, viewEnd: Date): ExpandedBlock[] {
  const recurring = slot.recurring;
  if (!recurring) return [];

  const results: ExpandedBlock[] = [];
  const effectiveStart = recurring.startDate ? new Date(recurring.startDate) : viewStart;
  const effectiveEnd = recurring.endDate ? new Date(recurring.endDate) : viewEnd;

  // Parse time from HH:mm format
  const [startHour, startMinute] = (slot.start ?? "00:00").split(":").map(Number);
  const [endHour, endMinute] = (slot.end ?? "00:00").split(":").map(Number);

  // Iterate through each day in the view range
  const current = new Date(Math.max(viewStart.getTime(), effectiveStart.getTime()));
  current.setHours(0, 0, 0, 0);

  const rangeEnd = new Date(Math.min(viewEnd.getTime(), effectiveEnd.getTime()));
  rangeEnd.setHours(23, 59, 59, 999);

  while (current <= rangeEnd) {
    const dayOfWeek = current.getDay();

    // Check if this day matches the pattern
    const dayCode = DAY_INDEX_TO_CODE[dayOfWeek];
    const matchesDay = !recurring.dayOfWeek || recurring.dayOfWeek.includes(dayCode as DayOfWeek);

    if (matchesDay) {
      const blockStart = new Date(current);
      blockStart.setHours(startHour, startMinute, 0, 0);

      const blockEnd = new Date(current);
      blockEnd.setHours(endHour, endMinute, 0, 0);

      results.push({ start: blockStart, end: blockEnd });
    }

    // Move to next day
    current.setDate(current.getDate() + 1);
  }

  return results;
}
